import { Fragment, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import { Pill } from "../Common";

const auTime = (s) => (s ? new Date(s).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const TONE = { active: "green", expired: "slate", revoked: "red" };
const HOW = { signin: "BOS HQ sign-in", sms: "SMS code", none: "No mobile on file" };

function AccessLog({ r }) {
  if (!r.access_log?.length) return <div className="px-3 py-2 text-xs text-slate-500">Not opened yet.</div>;
  return (
    <div className="space-y-1 px-3 py-2" data-testid={`shared-doc-log-${r.id}`}>
      {r.access_log.map((a, i) => <div key={i} className="text-xs text-slate-300">{auTime(a.at)} · {a.who} · {a.method === "sms" ? "SMS code" : "Signed in"} · IP {a.ip || "unknown"}</div>)}
    </div>
  );
}

export function SharedDocuments({ scope = "account" }) {
  const { user } = useAuth();
  const base = scope === "agency" ? "/agency/secure-docs" : "/secure-docs";
  const [rows, reload] = useGet(base);
  const [open, setOpen] = useState(null);
  const canRevoke = scope === "agency" || user?.role !== "staff";
  const revoke = async (r) => {
    if (!window.confirm(`Withdraw access to "${r.filename}" for ${r.recipient_email}? The link will stop working immediately.`)) return;
    try { await api.post(`${base}/${r.id}/revoke`); toast.success("Access revoked"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid={`shared-documents-${scope}`}>
      <p className="text-xs text-slate-500">Confidential documents sent by secure link. Recipients must sign in to BOS HQ or confirm a code texted to their mobile. Each opening is recorded with time and IP address.</p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm"><thead className="bg-white/[0.03] text-left text-xs text-muted-foreground"><tr>{["Sent", scope === "agency" && "Entity", "Document", "Recipient", "Check", "Expires", "Opened", "Status", ""].filter((h) => h !== false).map((h, i) => <th key={i} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>{(rows || []).map((r) => <Fragment key={r.id}>
            <tr className="border-t border-white/5" data-testid={`shared-doc-${r.id}`}>
              <td className="whitespace-nowrap px-3 py-2 text-xs">{auTime(r.created_at)}</td>{scope === "agency" && <td className="px-3 py-2 text-xs">{r.entity}</td>}
              <td className="max-w-[220px] truncate px-3 py-2 text-xs" title={r.filename}>{r.filename}</td>
              <td className="px-3 py-2 text-xs">{r.recipient_email}{r.recipient_phone && <div className="text-slate-500">{r.recipient_phone}</div>}</td>
              <td className="px-3 py-2 text-xs">{HOW[r.method]}</td>
              <td className="whitespace-nowrap px-3 py-2 text-xs">{auTime(r.expires_at)}</td>
              <td className="px-3 py-2 text-xs"><button className="text-[#C2A462] hover:underline" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`shared-doc-opened-${r.id}`}>{r.downloads} time{r.downloads === 1 ? "" : "s"}</button></td>
              <td className="px-3 py-2"><Pill tone={TONE[r.state]}>{r.state}</Pill>{r.revoked_by && <div className="text-[10px] text-slate-500">by {r.revoked_by}</div>}</td>
              <td className="px-3 py-2">{canRevoke && r.state === "active" && <Button size="sm" variant="outline" className="border-red-400/40 text-red-300" onClick={() => revoke(r)} data-testid={`shared-doc-revoke-${r.id}`}>Revoke</Button>}</td>
            </tr>
            {open === r.id && <tr className="bg-white/[0.02]"><td colSpan={9}><AccessLog r={r} /></td></tr>}
          </Fragment>)}
            {rows && !rows.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-xs text-muted-foreground">No confidential documents have been sent yet.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}
